import { booleanAttribute } from "#ce/utils/props";

export const props = Object.freeze({
  /**
   * Name of an sp-icon glyph rendered in the leading column. Set to "none-filled" or leave empty to omit the icon.
   *
   * @type {String}
   * @default undefined
   */
  icon: {
    type: String,
    default: undefined,
  },
  /**
   * Size token passed to the leading icon (small, medium, large).
   *
   * @type {String}
   * @default undefined
   */
  iconSize: {
    type: String,
    default: undefined,
  },
  /**
   * Visual layout variant. "default" applies the standard padding; "inline" tightens spacing for use inside content flow; "full" centres the broadcast within a max-width container and adds an outer padding rail.
   *
   * @type {String}
   * @default "default"
   * @values default, inline, full
   * @storybook-control select
   */
  variant: {
    type: String,
    default: "default",
    validator: (value) => ["default", "inline", "full"].includes(value),
  },
  /**
   * Semantic level driving the colour palette. "disclosure" is neutral with no background; "info" is informational blue; "warning" is cautionary yellow; "alert" is destructive red.
   *
   * @type {String}
   * @default "info"
   * @values disclosure, info, warning, alert
   * @storybook-control select
   */
  level: {
    type: String,
    default: "info",
    validator: (value) => ["disclosure", "info", "warning", "alert"].includes(value),
  },
  /**
   * Collapse the body content behind the title row. Clicking the title toggles between collapsed and expanded.
   *
   * @type {Boolean|String}
   * @default false
   */
  collapsable: booleanAttribute(),
  /**
   * HTML tag rendered as the title element. Use h2/h3/h4 to integrate with the surrounding page outline.
   *
   * @type {String}
   * @default "span"
   */
  titleTag: {
    type: String,
    default: "span",
  },
  /**
   * Primary action rendered as a trailing link, as an alternative to the sp-broadcast-action slot. Shape: `{ link: String, content: String }`. Pass via `.prop` because objects cannot be expressed as attribute strings. The `content` string is injected as HTML (`v-html`), so it must be trusted or pre-sanitized.
   *
   * @type {Object}
   * @default undefined
   */
  action: {
    type: Object,
    default: undefined,
  },
  /**
   * Render an "x" dismiss control in the trailing column. Clicking it hides the broadcast in place.
   *
   * @type {Boolean|String}
   * @default false
   */
  dismissable: booleanAttribute(),
  /**
   * Render the title and body as inline elements separated by a single space — useful for short banners.
   *
   * @type {Boolean|String}
   * @default false
   */
  inline: booleanAttribute(),
});
